import { MapPin, MessageCircle, Lock, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo, { BrandFooter } from "@/components/Logo";

export default function Landing() {
  const navigate = useNavigate();
  const handleLogin = () => navigate("/login");
  const handleSignup = () => navigate("/signup");

  return (
    <div className="min-h-screen grain-bg">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <Logo size="md" />
        <div className="flex items-center gap-2">
          <Button data-testid="header-signup-btn" onClick={handleSignup} variant="ghost" className="rounded-full">Sign up</Button>
          <Button data-testid="header-login-btn" onClick={handleLogin} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-5">Login</Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1 rounded-full text-xs font-semibold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
              HYPERLOCAL NEIGHBOURHOOD NETWORK
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Find the right <span className="text-teal-600">friend</span> for you.
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              MyLiveFriend helps you discover real people in your city — to make friends, go on a date, or book trusted paid companionship. All verified, all in your pincode.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-signup-btn" onClick={handleSignup} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 py-6 text-base font-semibold">
                Get started — it's free
              </Button>
              <Button data-testid="hero-login-btn" onClick={handleLogin} variant="outline" className="rounded-full px-6 py-6 text-base font-semibold border-slate-300">
                Login
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><Lock className="w-4 h-4" /> Private & secure</span>
              <span className="flex items-center gap-2"><Users className="w-4 h-4" /> 100% verified pincodes</span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-teal-100 to-amber-100 rounded-3xl -z-10"></div>
            <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-teal-600" />
                <span className="text-sm font-semibold text-slate-700">Mumbai · Andheri · 400053</span>
              </div>
              {[1,2,3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-slate-50/50">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-amber-400"></div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800 text-sm">Neighbour {i}</div>
                    <div className="text-xs text-slate-500">Same pincode · Available to chat</div>
                  </div>
                  <MessageCircle className="w-4 h-4 text-teal-600" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <section className="mt-20 grid md:grid-cols-3 gap-6">
          {[
            { icon: MapPin, title: "Pincode-first filtering", body: "Exact pincode, city, or area – find who's actually nearby." },
            { icon: Lock, title: "Subscription unlocks all", body: "Pick a plan to view contacts and chat without limits." },
            { icon: MessageCircle, title: "Real-time 1-on-1 chat", body: "Polling-based lightweight chat that just works." },
          ].map((f, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 border border-slate-200 hover-lift">
              <f.icon className="w-8 h-8 text-teal-600 mb-4" />
              <h3 className="font-heading font-bold text-lg text-slate-900">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="border-t border-slate-200 mt-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <BrandFooter />
            <p className="mt-3 text-xs text-slate-500 leading-relaxed max-w-xs">
              Hyperlocal connections — meet real neighbours, make friends, date thoughtfully or book trusted companionship.
            </p>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-2 text-sm">Explore</div>
            <ul className="space-y-1 text-sm text-slate-600">
              <li><Link to="/about" className="hover:text-teal-700">About</Link></li>
              <li><Link to="/faq" className="hover:text-teal-700">FAQ</Link></li>
              <li><Link to="/signup" className="hover:text-teal-700">Sign up</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-2 text-sm">Legal</div>
            <ul className="space-y-1 text-sm text-slate-600">
              <li><Link to="/about" className="hover:text-teal-700">Privacy Policy</Link></li>
              <li><Link to="/about" className="hover:text-teal-700">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold text-slate-900 mb-2 text-sm">Contact</div>
            <ul className="space-y-1 text-sm text-slate-600">
              <li><a href="mailto:hello@mylivefriend.com" className="hover:text-teal-700">Contact Us</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-400">© 2026 MyLiveFriend. All rights reserved.</div>
      </footer>
    </div>
  );
}
